import { useCallback, useMemo, useState } from 'react';

export interface ColumnDef {
  /** Identificador único (usarlo como key de la tabla real) */
  id: string;
  /** Etiqueta visible */
  label: string;
}

export interface UseColumnVisibilityResult {
  isVisible: (id: string) => boolean;
  toggle: (id: string) => void;
  showAll: () => void;
  hideAll: () => void;
  visibleIds: string[];
  activeCount: number;
  total: number;
  isAllVisible: boolean;
  isNoneVisible: boolean;
}

/**
 * useColumnVisibility — única fuente de verdad sobre qué columnas están
 * activas. Cualquier pantalla que necesite "mostrar/ocultar columnas"
 * consume este hook en vez de reimplementar su propio estado local.
 *
 * "Todo"/"Limpiar" y el contador leen del mismo estado derivado
 * (`isAllVisible`, `isNoneVisible`), por lo que nunca pueden desincronizarse.
 */
export function useColumnVisibility(
  columns: ColumnDef[],
  initiallyVisible: string[] = columns.map((c) => c.id),
): UseColumnVisibilityResult {
  const [visible, setVisible] = useState<Set<string>>(
    () => new Set(initiallyVisible),
  );

  const toggle = useCallback((id: string) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    setVisible(new Set(columns.map((c) => c.id)));
  }, [columns]);

  const hideAll = useCallback(() => {
    setVisible(new Set());
  }, []);

  const isVisible = useCallback((id: string) => visible.has(id), [visible]);

  const total = columns.length;
  const activeCount = visible.size;
  const visibleIds = useMemo(() => Array.from(visible), [visible]);

  return {
    isVisible,
    toggle,
    showAll,
    hideAll,
    visibleIds,
    activeCount,
    total,
    isAllVisible: activeCount === total,
    isNoneVisible: activeCount === 0,
  };
}
